import { Button, Select, Space, Table, Tag, Tooltip, notification } from 'antd';
import { CheckOutlined, CopyOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  ITEM_CATEGORY_LABEL,
  ITEM_KIND_SHORT_LABEL,
  ITEM_STATUS_OPTIONS,
  STAR_GROUP_LABEL,
  labelOf,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { ItemAdmin, ItemStatus } from 'pages/star-shared/types';
import { TABLE_EMPTY, formatNumber, imageUrl } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import { Ellipsis, NoWrap } from 'pages/star-shared/components/status-tags';
import { colors } from 'theme/tokens';
import ItemModal from './item-modal';

type ModalState = { open: boolean; item: ItemAdmin | null; clone: boolean };

const ItemsTab = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<ItemStatus | undefined>(undefined);
  const [modal, setModal] = useState<ModalState>({ open: false, item: null, clone: false });
  const { items, loading, setItems } = usePagedList<ItemAdmin>(API.starsItems, {}, { refreshKey });
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const visible = statusFilter ? items.filter((i) => i.status === statusFilter) : items;

  const changeStatus = async (item: ItemAdmin, status: ItemStatus) => {
    setSavingId(item.id);
    try {
      await AxiosUtils.patch(`${API.starsItems}/${item.id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status } : i)));
      notification.success({ message: 'Đã đổi trạng thái món.' });
    } catch (err) {
      notification.error({ message: mapStarError(err) });
      triggerRefresh();
    } finally {
      setSavingId(null);
    }
  };

  const columns: ColumnsType<ItemAdmin> = [
    {
      title: 'Ảnh',
      key: 'image',
      width: 72,
      render: (_, i) =>
        i.image ? (
          <img
            src={imageUrl(i.image)}
            alt=""
            width={56}
            height={56}
            style={{ objectFit: 'cover', borderRadius: 6, background: '#f5f5f5' }}
          />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 6, background: '#f5f5f5' }} />
        ),
    },
    {
      title: 'Món',
      key: 'name',
      width: 300,
      // Tên + nhóm hàng trên một dòng, mô tả xuống dòng riêng và cắt "…" theo đúng độ rộng ô
      // (trước đây Tag + mô tả 220px cố định nằm trong Space không co được → tràn sang cột "Loại").
      render: (_, i) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <span style={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{i.name}</span>
            <Tag style={{ flexShrink: 0, marginInlineEnd: 0 }}>
              {labelOf(ITEM_CATEGORY_LABEL, i.category)}
            </Tag>
          </div>
          {i.description && <Ellipsis value={i.description} width={400} />}
        </div>
      ),
    },
    {
      title: 'Loại',
      key: 'kind',
      width: 120,
      render: (_, i) => labelOf(ITEM_KIND_SHORT_LABEL, i.kind),
    },
    {
      title: 'Giá',
      key: 'price',
      width: 100,
      align: 'right',
      render: (_, i) => <StarAmount value={i.price} />,
    },
    {
      title: 'Kho',
      key: 'stock',
      width: 110,
      align: 'right',
      render: (_, i) =>
        i.stock === null || i.stock === undefined ? (
          <span style={{ color: colors.textSecondary }}>Không giới hạn</span>
        ) : (
          <span
            style={{
              color: i.stock <= 0 ? colors.dangerInk : i.stock <= 5 ? colors.warningInk : undefined,
            }}
          >
            {formatNumber(i.stock)}
          </span>
        ),
    },
    {
      title: 'Giới hạn/người',
      key: 'limits',
      width: 140,
      render: (_, i) => {
        const daily = i.perUserDailyLimit ? `${i.perUserDailyLimit}/ngày` : null;
        const monthly = i.perUserMonthlyLimit ? `${i.perUserMonthlyLimit}/tháng` : null;
        if (!daily && !monthly) return '—';
        // Không bẻ "10/" | "tháng" tại dấu gạch chéo.
        return (
          <span>
            {daily && <NoWrap>{daily}</NoWrap>}
            {daily && monthly ? ' · ' : null}
            {monthly && <NoWrap>{monthly}</NoWrap>}
          </span>
        );
      },
    },
    {
      title: 'Cần duyệt',
      key: 'needsApproval',
      width: 90,
      align: 'center',
      render: (_, i) =>
        i.needsApproval ? <CheckOutlined style={{ color: colors.successInk }} /> : '—',
    },
    {
      title: 'Nhóm',
      key: 'audience',
      width: 150,
      render: (_, i) => {
        const groups = i.audience?.starGroups || [];
        return groups.length ? (
          <Space size={4} wrap>
            {groups.map((g) => (
              <Tag key={g} color="blue">
                {labelOf(STAR_GROUP_LABEL, g)}
              </Tag>
            ))}
          </Space>
        ) : (
          'Tất cả'
        );
      },
    },
    {
      title: 'Hiệu lực',
      key: 'validity',
      width: 150,
      // Mỗi cụm giữ một dòng: xuống dòng sau "·", không tách "≤" khỏi con số.
      render: (_, i) => {
        const days = i.kind !== 'GOODS' && i.validDays ? `${i.validDays} ngày` : null;
        const cap =
          i.kind === 'TRANSFER_CARD' && i.transferMax
            ? `tặng ≤ ${formatNumber(i.transferMax)} ⭐`
            : null;
        if (!days && !cap) return '—';
        return (
          <span>
            {days && <NoWrap>{days}</NoWrap>}
            {days && cap ? ' · ' : null}
            {cap && <NoWrap>{cap}</NoWrap>}
          </span>
        );
      },
    },
    {
      title: 'Đã đổi',
      dataIndex: 'sold',
      key: 'sold',
      width: 80,
      align: 'right',
      render: (v) => v ?? 0,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_, i) => (
        <Select
          size="small"
          style={{ width: 110 }}
          value={i.status}
          options={ITEM_STATUS_OPTIONS}
          loading={savingId === i.id}
          disabled={savingId === i.id}
          onChange={(value) => changeStatus(i, value as ItemStatus)}
        />
      ),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'sort',
      key: 'sort',
      width: 70,
      align: 'right',
      render: (v) => v ?? 0,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_, i) => (
        <Space size={0}>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => setModal({ open: true, item: i, clone: false })}
            />
          </Tooltip>
          <Tooltip title="Nhân bản">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => setModal({ open: true, item: i, clone: true })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Mọi trạng thái"
          style={{ width: 160 }}
          options={ITEM_STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
        />
        <span style={{ color: colors.textSecondary }}>{visible.length} món</span>
        <div style={{ flex: 1 }} />
        <CustomButton
          bgColor="secondary-green"
          text="Thêm món"
          icon={<PlusOutlined />}
          onClick={() => setModal({ open: true, item: null, clone: false })}
        />
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={visible}
          loading={loading}
          locale={TABLE_EMPTY}
          scroll={{ x: 1610 }}
        />
      </div>
      <ItemModal
        open={modal.open}
        item={modal.item}
        clone={modal.clone}
        onClose={() => setModal({ open: false, item: null, clone: false })}
        onSuccess={triggerRefresh}
      />
    </div>
  );
};

export default ItemsTab;
